import { useEffect, useRef, useState } from 'react'
import { getEchoClient } from './echoClient'

export type ContractRealtimeUpdateType =
  | 'balance'
  | 'transaction'
  | 'subscription'

const ECHO_DEBUG =
  import.meta.env.DEV || String(import.meta.env.VITE_ECHO_DEBUG || '') === 'true'

function echoDevLog(...args: unknown[]) {
  if (ECHO_DEBUG) {
    console.info('[Echo contract]', ...args)
  }
}

/**
 * Подписка на private-канал contract.{id}.
 * Ожидаемые события (cross-cutting backend): contract.balance.changed,
 * contract.transaction.created|approved|rejected, lesson.subscription.consumed.
 */
export function useContractRealtime(
  contractId: number | null | undefined,
  onUpdate: (type: ContractRealtimeUpdateType) => void
) {
  const [isConnected, setIsConnected] = useState(false)
  const onUpdateRef = useRef(onUpdate)
  onUpdateRef.current = onUpdate

  useEffect(() => {
    if (!Number.isFinite(contractId)) {
      setIsConnected(false)
      return
    }

    const echo = getEchoClient()
    const channelName = `contract.${contractId}`

    setIsConnected(false)

    const channel = echo.private(channelName)

    type ChannelWithLifecycle = typeof channel & {
      subscribed?: (cb: () => void) => typeof channel
      error?: (cb: (err: unknown) => void) => typeof channel
    }
    const ch = channel as ChannelWithLifecycle

    if (typeof ch.subscribed === 'function') {
      ch.subscribed(() => {
        setIsConnected(true)
        echoDevLog(`Подписка на private-${channelName} подтверждена`)
      })
    } else {
      setIsConnected(true)
    }

    if (typeof ch.error === 'function') {
      ch.error((err) => {
        setIsConnected(false)
        console.error('[Echo] Ошибка подписки на канал', channelName, err)
      })
    }

    const refreshBalance = () => onUpdateRef.current('balance')
    const refreshTransaction = () => onUpdateRef.current('transaction')
    const refreshSubscription = () => onUpdateRef.current('subscription')

    channel
      .listen('.contract.balance.changed', refreshBalance)
      .listen('.contract.transaction.created', refreshTransaction)
      .listen('.contract.transaction.approved', refreshTransaction)
      .listen('.contract.transaction.rejected', refreshTransaction)
      .listen('.lesson.subscription.consumed', refreshSubscription)

    echoDevLog(`Подписка на private-${channelName}`)

    return () => {
      try {
        echo.leave(channelName)
      } catch {
        // ignore
      }
      setIsConnected(false)
    }
  }, [contractId])

  return { isConnected }
}
